/**
 * Text Tool Settings Panel
 * Settings for font, size, weight, color, and alignment
 */

import React from 'react';
import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useToolbarStore } from '@/stores/toolbarStore';
import { FontPicker } from '@/components/ui/FontPicker';
import { ColorSwatch } from '@/components/ui/BrushPicker';

const FONT_WEIGHTS = [
  { label: 'Light', value: 300 },
  { label: 'Regular', value: 400 },
  { label: 'Medium', value: 500 },
  { label: 'Semibold', value: 600 },
  { label: 'Bold', value: 700 },
];

export function TextToolSettings() {
  const { textDefaults, updateTextDefaults } = useToolbarStore();

  return (
    <div className="space-y-3">
      {/* Font family */}
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Font
        </label>
        <FontPicker
          value={textDefaults.fontFamily}
          onChange={(fontFamily) => updateTextDefaults({ fontFamily })}
        />
      </div>

      {/* Font size and weight */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
            Size
          </label>
          <input
            type="number"
            value={textDefaults.fontSize}
            onChange={(e) => updateTextDefaults({ fontSize: parseInt(e.target.value) || 16 })}
            min={8}
            max={200}
            className="w-full bg-navy border border-white/10 rounded-lg px-2 py-1.5 text-sm text-fg focus:outline-none focus:border-primary/50"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
            Weight
          </label>
          <select
            value={textDefaults.fontWeight}
            onChange={(e) => updateTextDefaults({ fontWeight: parseInt(e.target.value) })}
            className="w-full bg-navy border border-white/10 rounded-lg px-2 py-1.5 text-sm text-fg focus:outline-none focus:border-primary/50"
          >
            {FONT_WEIGHTS.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Color */}
      <div className="min-w-0">
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Color
        </label>
        <ColorSwatch
          value={textDefaults.color}
          onChange={(color) => updateTextDefaults({ color })}
        />
      </div>

      {/* Text alignment */}
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
          Align
        </label>
        <div className="flex gap-1">
          {(['left', 'center', 'right'] as const).map((align) => {
            const Icon = align === 'left' ? AlignLeft : align === 'center' ? AlignCenter : AlignRight;
            return (
              <button
                key={align}
                onClick={() => updateTextDefaults({ textAlign: align })}
                className={`
                  flex-1 py-1.5 rounded-lg flex items-center justify-center transition-colors
                  ${textDefaults.textAlign === align
                    ? 'bg-primary/20 text-primary'
                    : 'bg-navy text-fg-muted hover:bg-white/10 hover:text-fg'
                  }
                `}
              >
                <Icon size={16} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
